import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, NotebookPen } from 'lucide-react';
import { descreverCampo, motivoValido, MOTIVO_MINIMO, type CampoAlterado } from '../lib/alteracoesCadastro';

// Pede o motivo antes de gravar uma mudança no cadastro de UC em operação. O motivo
// aparece para o Faturamento no cálculo e na emissão do boleto (src/lib/alteracoesCadastro.ts).

const MotivoAlteracaoModal = ({ campos, salvando, onConfirmar, onCancelar }: {
    campos: CampoAlterado[];
    salvando?: boolean;
    onConfirmar: (motivo: string) => void;
    onCancelar: () => void;
}) => {
    const [motivo, setMotivo] = useState('');
    const valido = motivoValido(motivo);

    return createPortal(
        // O portal sai do DOM do formulário da UC, mas o React continua subindo os
        // eventos pela árvore: sem o stopPropagation, o submit daqui disparava o
        // submit do cadastro da UC (e o clique fora fechava a janela de trás).
        <div className="fixed inset-0 z-[3100] bg-black/50 flex items-center justify-center p-4" onClick={e => { e.stopPropagation(); onCancelar(); }}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="motivo-alteracao-titulo"
                className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg p-6"
                onClick={e => e.stopPropagation()}
            >
                <h3 id="motivo-alteracao-titulo" className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <NotebookPen size={18} className="text-primary" /> Por que esta mudança?
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                    O Faturamento vai ver isto ao calcular e emitir o boleto desta UC, com seu nome e a data.
                </p>

                <ul className="mt-4 rounded-xl bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700 px-3 py-2 space-y-1 max-h-48 overflow-y-auto">
                    {campos.map(c => (
                        <li key={c.campo} className="text-sm text-gray-800 dark:text-gray-200 break-words">{descreverCampo(c)}</li>
                    ))}
                </ul>

                <form
                    className="mt-4"
                    onSubmit={e => { e.preventDefault(); e.stopPropagation(); if (valido && !salvando) onConfirmar(motivo.trim()); }}
                >
                    <label htmlFor="motivo-alteracao" className="block text-xs font-bold text-gray-700 dark:text-white uppercase mb-1">Motivo</label>
                    <textarea
                        id="motivo-alteracao"
                        value={motivo}
                        onChange={e => setMotivo(e.target.value)}
                        rows={3}
                        autoFocus
                        placeholder="Ex.: desconto de 25% negociado com o cliente para não cancelar. Pedido do Ítalo no chamado de 28/10."
                        className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-800 dark:text-white focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                    />
                    <p className={`text-[11px] mt-1 ${valido ? 'text-gray-400' : 'text-gray-500 dark:text-gray-400'}`}>
                        Escreva o que aconteceu e quem pediu (pelo menos {MOTIVO_MINIMO} letras).
                    </p>

                    <div className="flex gap-2 justify-end mt-4">
                        <button
                            type="button"
                            onClick={onCancelar}
                            className="px-4 py-2 rounded-lg border border-gray-200 dark:border-slate-700 text-sm font-medium text-gray-600 dark:text-gray-300"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={!valido || salvando}
                            className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-primary disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                        >
                            {salvando ? <Loader2 size={14} className="animate-spin" /> : null} Salvar com motivo
                        </button>
                    </div>
                </form>
            </div>
        </div>,
        document.body,
    );
};

export default MotivoAlteracaoModal;
